import { BaseControl } from "../controls";
import { WidgetInterface } from "./WidgetInterface";

/**
 * Widget Base Class
 * @abstract
 * @implements {WidgetInterface}
 */
export class BaseWidget extends WidgetInterface {

    /** @type {string} */
    id = '';

    /** @type {string|null} Firebase docid */
    docid = null;

    /** @type {boolean} */
    deleted = false;

    /** @type {string|null} */
    parent = null;
    
    /** @type {number} */
    order = 0;

    /** @type {boolean} */
    editable = false;

    /** @type {boolean} Whether any controls where changed */
    isDirty = false;

    constructor(settings = {}, editable = false) {
        super();

        /**
         * The actual values for the widget controls
         * @type {Record<string, any>}
         */
        this.settings = settings;

        /** @type {Record<string, BaseControl>} */
        this.controls = {};

        /** @type {boolean} */
        this.editable = editable;

        this.initialize();
    }

    initialize() {
        this.name = this.getName();
        this.icon = this.getIcon();
        // this.category = this.getCategory();
        
        this.controls = this.getControls();

        /* Initialize settings with default control values */
        Object.entries(this.controls).forEach(([key, control]) => {
            if (!(key in this.settings) && control.defaultValue !== undefined) {
                this.settings[key] = control.defaultValue;
            }
        });
    }

    /**
     * Get a setting value for a given control
     * @param {string} key 
     * @returns {any}
     */
    getSetting(key) {
        return this.settings[key] ?? this.controls?.[key]?.defaultValue;
    }

    /**
     * Get a setting formatted as CSS value (e.g. '10px')
     * Only supports controls like NumberCssControl that have a { value, unit } structure
     * @param {string | { value: number, unit: string }} keyOrProperty 
     * @returns {string}
     */
    getCssValue(keyOrProperty) {
        const value = typeof keyOrProperty === 'string' 
            ? this.getSetting(keyOrProperty) 
            : keyOrProperty;

        if (typeof value === 'object' && value !== null && 'value' in value && 'unit' in value) {
            if (value.unit === 'auto') {
                return 'auto';
            }
            
            return `${value.value}${value.unit}`;
        }

        return value;
    }

    /**
     * Set a setting value for a given control
     * @param {string} key
     * @param {any} value 
     */
    setSetting(key, value) {
        if (!this.editable) {
            return;
        }

        this.isDirty = true;
        this.settings[key] = value;
    }

    /**
     * Styles to apply to the parent's CanvasNode (e.g. for width/height controls)
     * @returns {Partial<CSSStyleDeclaration>}
     */
    getStyles() {
        return {};
    }
}
